import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Database,
  FileJson,
  Moon,
  Play,
  RefreshCw,
  Shield,
  SlidersHorizontal,
  Sun,
  Terminal,
} from "lucide-react";
import { Logo } from "./Logo";
import { useConvex, useMutation, useQuery } from "convex/react";
import { api, ApiError } from "../api/client";
import { api as convexApi } from "../lib/convexApi";
import { routeLinkHandler } from "../routeNav";
import { adminStore } from "../stores/adminStore";
import type { AdminSnapshot } from "../types";
import { toggleTheme as flipTheme } from "../theme";
import { useEntitlements } from "../hooks/useEntitlements";
import { ADMIN_ONLY_TABS } from "./adminConsole/types";
import { PlanBillingTab } from "./adminConsole/PlanBillingTab";
import { DlpPatternsTab } from "./adminConsole/DlpPatternsTab";
import { TeamRolesTab } from "./adminConsole/TeamRolesTab";
import { GithubIntegrationTab } from "./adminConsole/GithubIntegrationTab";
import { CredentialsVaultTab } from "./adminConsole/CredentialsVaultTab";
import { ConnectAgentsTab } from "./adminConsole/ConnectAgentsTab";
import { CostDashboardTab } from "./adminConsole/CostDashboardTab";
import { EgressAllowlistTab } from "./adminConsole/EgressAllowlistTab";
import { AdminTabBoundary } from "./adminConsole/AdminTabBoundary";

type AdminTab =
  | "overview"
  | "packs"
  | "firewall"
  | "pr"
  | "agents"
  | "activity"
  | "enforcement"
  | "dlp-patterns"
  | "plan-billing"
  | "team-roles"
  | "github-integration"
  | "credentials-vault"
  | "connect-agents"
  | "cost-dashboard"
  | "egress";

const BASE_TABS: Array<{ key: AdminTab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "packs", label: "Policy packs" },
  { key: "firewall", label: "Firewall" },
  { key: "pr", label: "PR policies" },
  { key: "agents", label: "Agents" },
  { key: "activity", label: "Activity" },
  { key: "enforcement", label: "Enforcement" },
  { key: "dlp-patterns", label: "DLP patterns" },
];

// Per-tab entitlement floor (cumulative base ⊂ pro ⊂ security). Matches the
// backend gate each tab's data source enforces:
//   - base: plan-billing (billing reads have no plan gate), team-roles (Clerk
//     SDK only).
//   - pro: github-integration (github.queries.* moved security→pro), activity
//     (audit.queries.recent is plan:"pro"-gated).
//   - security: everything that calls requireOrg(ctx,{plan:"security"}).
// The UI gates buttons + skips queries to MATCH these so a paying base/pro
// subscriber reaches what they own without 402-crashing a security pane.
type MinTier = "base" | "pro" | "security";
const TAB_MIN_TIER: Record<AdminTab, MinTier> = {
  // base
  "plan-billing": "base",
  "team-roles": "base",
  // pro
  "github-integration": "pro",
  activity: "pro",
  // security
  overview: "security",
  packs: "security",
  firewall: "security",
  pr: "security",
  agents: "security",
  enforcement: "security",
  "dlp-patterns": "security",
  "credentials-vault": "security",
  "connect-agents": "security",
  "cost-dashboard": "security",
  egress: "security",
};

// Why did the admin snapshot fail? Three outcomes, because collapsing them is
// what produced the bug this PR exists to fix — and, twice over, the bugs in its
// own earlier rounds.
//
//   "authz"     — 401, 402, 403. The only statuses that are a verdict on the
//                 CALLER. These, and only these, show the red banner.
//   "absent"    — nothing is serving /api/* here. Two signatures: an ApiError
//                 with a 2xx status, and a throw that isn't an ApiError at all.
//   "unhealthy" — a host answered with a real HTTP error (404, 429, 5xx…). It
//                 EXISTS; it is just not serving this right now.
//
// The 2xx-ApiError rule is exact, not heuristic: request() throws only for a
// non-ok response OR an unparseable body, so an ApiError carrying a 2xx status
// can only mean the body would not parse — which is Cloudflare's SPA fallback
// signature (index.html, HTTP 200) and nothing else. Deriving it from the STATUS
// avoids the dead end of narrowing on MALFORMED_RESPONSE_MESSAGE, which is not
// exclusive to the fallback: client.ts parses the body before checking
// response.ok (client.ts:94 vs :98), so an auth proxy's HTML interstitial on a
// 403 raises that message too, and message-narrowing un-gated the console on a
// genuine refusal.
//
// Splitting "absent" from "unhealthy" is Codex's finding on b04e3db, and it is
// right: a 502 from the desktop app's own host was labelled "Running without a
// local Steerly host", which tells someone already running the desktop app to go
// get the desktop app. Both still fall open — access is never revoked for an
// operational failure — but the note now says which one happened.
//
// EVERY non-ApiError is logged, with no filtering by error type. An earlier
// revision skipped TypeError on the theory that it meant a rejected fetch;
// Codex pointed out that ordinary client defects ("Cannot read properties of
// undefined") are TypeErrors too, so that filter silenced precisely the class it
// was added to surface — the fix reproducing its own bug one layer down. The
// hosted web app does not even produce rejected fetches (it answers 200 HTML, an
// ApiError), so there was no noise to suppress in the first place.
//
// Falling open on a throw we don't recognise is deliberate: telling a legitimate
// admin they have no access because our own client threw is the worse lie, and
// it is safe because `authorized` is presentation only — role comes from the
// Convex myRole query (:190), tier from entitlements (:202), and every pane's
// data is enforced server-side regardless of what this returns.
type HostFailure = "authz" | "absent" | "unhealthy";

function classifyHostFailure(error: unknown): HostFailure {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 402 || error.status === 403) return "authz";
    if (error.status >= 200 && error.status < 300) return "absent";
    return "unhealthy";
  }
  console.error("[admin] admin snapshot threw a non-HTTP error:", error);
  return "absent";
}

// Every tab key, so a deep link like `/admin#github-integration` can select the
// matching tab on load. Without this the GitHub OAuth callback (which redirects
// to `#github-integration?installed=…`) would land on Overview and the
// GithubIntegrationTab — which completes the Convex connect — would never mount.
const VALID_TAB_KEYS = new Set<AdminTab>([
  ...BASE_TABS.map((t) => t.key),
  ...ADMIN_ONLY_TABS.map((t) => t.key as AdminTab),
]);

// The lowest-tier tab that any entitled admin can always reach (base). Used
// as the fallback landing tab when the resolved initial tab isn't accessible
// to the user's tier (e.g. a base user with a deep link to #overview).
const FALLBACK_TAB: AdminTab = "plan-billing";

// Reads the URL hash deep link (e.g. /admin#github-integration) into a tab
// key. `canAccess` filters by the user's tier so a base/pro user who deep-links
// a higher-tier tab lands on the lowest tab they CAN use instead of a locked
// (and query-skipped, hence empty) pane. When the tier is still loading we
// don't yet know what's accessible — the caller passes a permissive predicate
// then so the deep link survives until entitlement resolves.
function initialAdminTab(canAccess: (tab: AdminTab) => boolean): AdminTab {
  if (typeof window === "undefined") return canAccess("overview") ? "overview" : FALLBACK_TAB;
  const seg = (window.location.hash || "").replace(/^#/, "").split("?")[0];
  const resolved = VALID_TAB_KEYS.has(seg as AdminTab) ? (seg as AdminTab) : "overview";
  return canAccess(resolved) ? resolved : FALLBACK_TAB;
}

type ReloadResult = {
  ok?: unknown;
  packCount?: unknown;
  lastError?: unknown;
};

export function AdminConsoleView() {
  const snapshot = adminStore.useStore();

  // Cumulative-tier entitlement (base ⊂ pro ⊂ security). Gates which tab
  // BUTTONS render and — critically — which security-gated Convex/REST reads
  // fire (skipped for non-security so they never 402). Defaults to all-false
  // while loading so we never momentarily fire a security query the user
  // can't make.
  const ent = useEntitlements();
  const hasBase = ent.kind === "ready" && ent.hasBase;
  const hasPro = ent.kind === "ready" && ent.hasPro;
  const hasSecurity = ent.kind === "ready" && ent.hasSecurity;

  // Does the user's entitlement cover this tab's minTier? Used for both the
  // tab-button filter and the initial-tab fallback. While entitlement is still
  // loading every tab is treated as inaccessible (returns false) — but the
  // initial-tab resolver below passes a permissive predicate during loading so
  // a deep link isn't dropped before we know the tier.
  const tierCovers = (tab: AdminTab): boolean => {
    const min = TAB_MIN_TIER[tab];
    if (min === "base") return hasBase;
    if (min === "pro") return hasPro;
    return hasSecurity;
  };

  // Resolve the initial tab once entitlement is known. While loading, allow the
  // deep-linked tab through (permissive) so e.g. the GitHub OAuth callback hash
  // survives; an effect below re-runs the resolver when entitlement settles.
  const [activeTab, setActiveTab] = useState<AdminTab>(() =>
    initialAdminTab(ent.kind === "ready" ? tierCovers : () => true)
  );
  const [loading, setLoading] = useState(false);
  const [testCommand, setTestCommand] = useState("cat .env");
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [reloadResult, setReloadResult] = useState<ReloadResult | null>(null);

  // Gate admin-only tabs by the backend-synthesized isAdmin so personal/self-host
  // workspaces (which have no Clerk org) still reach Egress / Vault / MCP / Team.
  // requireOrg() promotes personal workspaces to admin; Clerk org-membership role
  // is used for real orgs — the backend mirrors this in myRole. While the query
  // is still loading (undefined), isOrgAdmin stays false (base tabs only) and
  // expands reactively when the result arrives.
  const myRoleResult = useQuery((convexApi as any).billing.queries.myRole, {});
  const isOrgAdmin = myRoleResult?.isAdmin === true;

  // Two-axis tab visibility: role (isOrgAdmin) AND tier (tierCovers). The role
  // axis is unchanged — admin-only tabs still require isOrgAdmin. The tier axis
  // is new: each tab's button only renders if the user's entitlement covers its
  // minTier. A base admin sees plan-billing + team-roles; a pro admin adds
  // github-integration + activity; a security admin sees everything (unchanged
  // from before this fix).
  const TABS = (
    isOrgAdmin
      ? [...BASE_TABS, ...ADMIN_ONLY_TABS.map((t) => ({ key: t.key as AdminTab, label: t.label }))]
      : BASE_TABS
  ).filter((t) => tierCovers(t.key));

  // Re-resolve the active tab once entitlement settles (or whenever it changes).
  // If the current tab isn't covered by the user's tier — e.g. a base user
  // whose deep link initially landed on a security tab before entitlement
  // resolved — bounce them to the lowest accessible tab so they never sit on a
  // query-skipped, empty (or about-to-402) pane.
  useEffect(() => {
    if (ent.kind !== "ready") return;
    if (!tierCovers(activeTab)) {
      setActiveTab(initialAdminTab(tierCovers));
    }
    // tierCovers closes over the entitlement booleans; depend on those.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ent.kind, hasBase, hasPro, hasSecurity]);

  useEffect(() => {
    // The admin REST snapshot (getAdminSnapshot) is a security-gated endpoint
    // (requireSecurity). Only fetch it for security users — otherwise it 402s
    // and trips the unauthorized banner over the base/pro panes. Non-security
    // users get a synthesized authorized=true empty snapshot so plan-billing,
    // team-roles, github-integration, and activity render normally.
    if (hasSecurity) {
      void refresh();
    } else if (ent.kind === "ready") {
      setAdminSnapshot({ authorized: true, packs: [], auditEvents: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSecurity, ent.kind]);

  // Spec 5.1 PR 2 — policy/firewall/risk/agents/enforcement reads
  // come from Convex queries (reactive). The legacy adminStore still
  // holds auditEvents + authorized; those tabs migrate in PR 4.
  // Pattern #14 from [[feedback-spec5-pr54-64-review-patterns]]:
  // Convex snapshots merge with adminStore — we don't clobber the
  // store with these reads, we read them into locals.
  // CRITICAL — all five of these reads call requireOrg(ctx,{plan:"security"})
  // on the backend and would 402 for a base/pro user. Convex useQuery's "skip"
  // sentinel makes the hook a no-op (returns undefined, fires no request), so
  // we pass "skip" whenever !hasSecurity. The owning tabs (packs/firewall/pr/
  // agents/enforcement/overview) are also hidden for those users, so the
  // undefined result is never rendered — but skipping the FETCH is what keeps
  // the pane from 402-crashing.
  const packsData = useQuery(convexApi.policy.queries.packs, hasSecurity ? {} : "skip") as
    | {
        packs: Array<{
          id: string;
          packId: string;
          name: string;
          contentHash: string;
          ruleCount: number;
          uploadedAt: number;
          uploadedBy: string;
          layer?: string;
          builtin?: boolean;
        }>;
      }
    | undefined;
  const firewallData = useQuery(convexApi.firewall.queries.listRules, hasSecurity ? {} : "skip") as
    | { rules: Array<Record<string, unknown>> }
    | undefined;
  const prData = useQuery(convexApi.policy.queries.prPolicies, hasSecurity ? {} : "skip") as
    | { policies: Array<Record<string, unknown>> }
    | undefined;
  const agentsData = useQuery(convexApi.policy.queries.agentPolicies, hasSecurity ? {} : "skip") as
    | { agents: Record<string, { mode?: string; reason?: string }> }
    | undefined;
  // RC-5: reactive enforcement mode from Convex (off/observe/ask/block).
  // The legacy REST snapshot still provides the four-tier visibility data
  // (shims/DYLD/ES/LLM-tap); the Convex query is the single source of
  // truth for the mode field that the setEnforcementMode mutation writes.
  const enforcementModeData = useQuery(
    convexApi.policy.queries.getEnforcement,
    hasSecurity ? {} : "skip"
  ) as { mode: string; posture?: string; updatedBy?: string; updatedAt?: number } | undefined;
  // Pro-gated audit feed for the Activity tab. api.audit.queries.recent calls
  // requireOrg(ctx,{plan:"pro"}) and returns the raw auditEvents rows (desc by
  // recordedAt). Skipped for base users (Activity tab is hidden for them); pro
  // and security users get a live reactive feed instead of the security-gated
  // REST snapshot's auditEvents.
  const auditRecent = useQuery(
    (convexApi as any).audit.queries.recent,
    hasPro ? { limit: 100 } : "skip"
  ) as Array<Record<string, unknown>> | undefined;
  const setEnforcementModeMutation = useMutation(convexApi.policy.mutations.setEnforcementMode);
  const [settingMode, setSettingMode] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);

  const convex = useConvex();
  const reloadPacksMutation = useMutation(convexApi.policy.mutations.reload);

  // C72-7: packsData now returns built-ins + org-uploaded rows with a
  // `layer` discriminator. Split them so the admin UI can render the
  // built-ins as non-editable baseline + custom packs above.
  const packs = useMemo(
    () =>
      (packsData?.packs ?? []).map((p) => ({
        id: p.packId,
        name: p.name,
        layer: p.layer ?? "org",
        ruleCount: p.ruleCount,
        contentHash: p.contentHash,
        enforced: true,
        builtin: Boolean(p.builtin),
      })) as Array<Record<string, unknown>>,
    [packsData]
  );
  // C72-1: firewall rules now interleave built-ins (priority < 0) with
  // org-custom rows. Split for the UI so admins can see the deny set
  // they cannot edit, separately from their own rules.
  const allFirewall = (firewallData?.rules ?? []) as Array<Record<string, unknown>>;
  const firewallDefaults = allFirewall.filter((r) => Boolean(r.builtin));
  const firewallAdmin = allFirewall.filter((r) => !r.builtin);
  const firewallRules = firewallAdmin;
  const prDefaults: Array<Record<string, unknown>> = [];
  const prAdmin = (prData?.policies ?? []) as Array<Record<string, unknown>>;
  // C72-5: layer built-in agent CLIs (from chatDrivers/) under the
  // override list so a fresh org sees the registry instead of "no
  // agents". Override rows take precedence — same name keeps the
  // override semantics; built-ins fill the empty slots.
  const agentRows = useMemo(() => {
    const overrides = agentsData?.agents ?? {};
    const builtins =
      (agentsData as { builtins?: Array<{ name: string; command: string }> } | undefined)
        ?.builtins ?? [];
    const overrideRows = Object.entries(overrides).map(([name, val]) => ({
      name,
      command: builtins.find((b) => b.name === name)?.command ?? "",
      type: "override",
      policy: val.mode ? `${val.mode}${val.reason ? ` — ${val.reason}` : ""}` : "default",
    }));
    const overrideNames = new Set(overrideRows.map((r) => r.name));
    const builtinRows = builtins
      .filter((b) => !overrideNames.has(b.name))
      .map((b) => ({
        name: b.name,
        command: b.command,
        type: "builtin",
        policy: "default",
      }));
    return [...builtinRows, ...overrideRows];
  }, [agentsData]);
  const enforcement = asRecord(snapshot.enforcement);
  const auditEvents = (snapshot.auditEvents || []) as Array<Record<string, unknown>>;
  // Activity tab (pro): normalize the reactive audit.queries.recent rows into
  // the field shape EventList already understands. The Convex auditEvents rows
  // expose { type, subject, details, at }; EventList reads recordedAt/detail.
  // `details` is `v.any()` (an object) — flatten its common message-ish field
  // to a string so the row body shows something readable, falling back to the
  // subject. The Overview tab keeps its own (security REST snapshot) auditEvents.
  const activityEvents = useMemo<Array<Record<string, unknown>>>(() => {
    if (!auditRecent) return [];
    return auditRecent.map((row) => {
      const details = row.details as Record<string, unknown> | string | undefined;
      const detailText =
        typeof details === "string"
          ? details
          : details && typeof details === "object"
            ? (details.message ?? details.reason ?? details.detail ?? JSON.stringify(details))
            : undefined;
      return {
        id: row._id ?? row.id,
        type: row.type,
        recordedAt: row.at,
        detail: detailText ?? row.subject,
      };
    });
  }, [auditRecent]);
  const authorized = snapshot.authorized;

  async function refresh() {
    setLoading(true);
    setReloadResult(null);
    try {
      const next = await api.getAdminSnapshot();
      setAdminSnapshot(next);
    } catch (error) {
      // "No host behind this origin" is not "you are not authorized".
      //
      // The snapshot probes /api/admin/whoami, a legacy Bun-host route. On the
      // hosted web app there is no host: Cloudflare's SPA fallback answers with
      // index.html, JSON.parse throws, and this catch used to set
      // authorized:false — which gates EVERY pane, including the ones that read
      // Convex directly and would have rendered fine. The net effect was that
      // the whole Admin Console was dead on app.steerly.ai for exactly the users
      // who pay for it (only security-plan users run this probe at all; everyone
      // else already gets the synthesized authorized:true below).
      //
      // So keep the Convex panes alive for both operational outcomes, say WHICH
      // one happened, and reserve the red "not authorized" banner for the three
      // statuses that are actually a verdict on the caller: 401, 402, 403 (see
      // classifyHostFailure above for why nothing else qualifies).
      const failure = classifyHostFailure(error);
      if (failure !== "authz") {
        setAdminSnapshot({
          authorized: true,
          packs: [],
          auditEvents: [],
          hostUnavailable: true,
          hostFailure: failure,
          hostStatus: error instanceof ApiError ? error.status : undefined,
        });
      } else {
        setAdminSnapshot({
          authorized: false,
          packs: [],
          auditEvents: [],
          error: error instanceof Error ? error.message : "Admin console unavailable",
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function classifyCommand() {
    const command = testCommand.trim();
    if (!command) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      // policy.queries.classifyCommand, NOT firewall.queries.testCommand.
      // The pane's own subtitle promises classification "against the production
      // policy path", and testCommand only knows the 7 builtin firewall denies
      // — so it answered ALLOW for the 35+ commands the builtin policy packs
      // deny (e.g. `mkfs /dev/sda`) and never saw an active exception. The
      // unified classifier evaluates every layer the engine enforces.
      const result = (await convex.query(convexApi.policy.queries.classifyCommand, {
        command,
      })) as {
        decision: string;
        enforced: boolean;
        mode: string;
        matched: { layer: string; ruleId: string; reason: string | null } | null;
        matches?: Array<{ ruleId: string; live?: boolean; decision?: string }>;
        surfaces?: { terminal?: { decision: string } };
      };
      // Adapt to the {decision, matchedRule, reason} shape the existing
      // renderer (classifyResult / decisionClass) already understands.
      //
      // `enforced` reflects the ORG enforcement mode, which gates the egress
      // stage and reporting — not whether the PTY will decline the command
      // (that is the per-terminal enforce flag). Saying "not enforced" flatly
      // would tell an admin a command runs when a terminal started with
      // enforcement on still declines it.
      // From the TERMINAL surface, not the headline: a pack-sourced verdict the
      // terminal never sees was being reported as something the terminal acts
      // on — and "declines" was printed even for an `ask`.
      const terminal = result.surfaces?.terminal;
      const notEnforced =
        terminal && terminal.decision !== "allow" && !result.enforced
          ? ` — org enforcement mode is ${result.mode} (gates the egress stage; a terminal with enforcement on still ${terminal.decision === "ask" ? "holds this for approval" : "declines this"})`
          : "";
      // This pane is where these rules are authored, so it is the one place an
      // admin must be told when the rule they just wrote governs nothing:
      // Convex firewallRules have no enforcement path (the REST writer that fed
      // the PTY's rule store returns 410, and nothing syncs Convex rows back).
      // Split by intent: "will not stop this command" is right for a deny rule
      // and the opposite of the truth for an allow rule, whose purpose is to let
      // the command through.
      const dead = (result.matches ?? []).filter((m) => m.live === false);
      const deadBlocking = dead.filter((m) => m.decision !== "allow").map((m) => m.ruleId);
      const deadAllowing = dead.filter((m) => m.decision === "allow").map((m) => m.ruleId);
      const deadNote = [
        deadBlocking.length
          ? `${deadBlocking.join(", ")} matched but ${deadBlocking.length === 1 ? "has" : "have"} no enforcement path and will not stop this command`
          : "",
        deadAllowing.length
          ? `${deadAllowing.join(", ")} matched but ${deadAllowing.length === 1 ? "is" : "are"} not consulted by anything that runs`
          : "",
      ]
        .filter(Boolean)
        .join("; ");
      setTestResult({
        decision: result.decision,
        matchedRule: result.matched?.ruleId ?? null,
        reason: result.matched
          ? // `||` not `??`: a rule whose description is an empty string should
            // fall back to the ruleId, not render a blank reason. `??` only
            // catches null/undefined, and firewallRules.description is a
            // free-text admin field that can legitimately be "".
            `${result.matched.reason || result.matched.ruleId} [${result.matched.layer}]${notEnforced}${deadNote ? ` — NOTE: ${deadNote}` : ""}`
          : `no rule matched (default-allow)${deadNote ? ` — NOTE: ${deadNote}` : ""}`,
      });
    } catch (error) {
      setTestError(error instanceof Error ? error.message : "Command classification failed");
    } finally {
      setTesting(false);
    }
  }

  async function reloadPolicies() {
    setReloadResult(null);
    try {
      // Spec 5.1 PR 2 — replaces /api/admin/policies/reload POST.
      const result = await reloadPacksMutation({});
      setReloadResult({
        ok: true,
        packCount: result.refreshed,
      });
      await refresh();
    } catch (error) {
      setReloadResult({
        ok: false,
        lastError: error instanceof Error ? error.message : "Policy reload failed",
      });
    }
  }

  return (
    <div className="admin-shell react-admin-shell">
      <header className="admin-topbar">
        <a
          className="brand"
          href="/"
          aria-label="Steerly Workbench"
          onClick={routeLinkHandler("/")}
        >
          <div className="brand-mark" aria-hidden="true">
            <Logo />
          </div>
          <div>
            <strong>Steerly</strong>
            <span>Admin console</span>
          </div>
        </a>
        <nav className="admin-tabs" role="tablist" aria-label="Admin console tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`admin-tab${activeTab === tab.key ? " is-active" : ""}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="admin-topbar__actions">
          <a
            className="button secondary"
            href="/security-room"
            onClick={routeLinkHandler("/security-room")}
          >
            Security Room
          </a>
          <a className="button secondary" href="/" onClick={routeLinkHandler("/")}>
            Workbench
          </a>
          <button
            className="theme-toggle"
            type="button"
            aria-label="Toggle theme"
            onClick={() => flipTheme()}
          >
            <Sun className="icon-sun" />
            <Moon className="icon-moon" />
          </button>
          <button className="button primary" type="button" onClick={() => void refresh()}>
            <RefreshCw size={15} aria-hidden="true" />
            {loading ? "Refreshing" : "Refresh"}
          </button>
        </div>
      </header>

      <main className="admin-content">
        {!authorized && !loading ? (
          <section className="admin-error" role="alert">
            <strong>Admin access is not active.</strong>
            <p>{formatAdminError(snapshot.error)}</p>
          </section>
        ) : null}

        {/*
          The host-backed surface is unavailable. Everything Convex-backed below
          still works. What does NOT, and what this note must therefore name
          without overstating: the Enforcement tier detail and the host-side
          audit feed (both fed by the legacy /api/admin/* routes), plus
          FINISHING a GitHub App install.

          "Finishing", not "starting" — #278 moved state minting off the host
          into Convex (github.mutations.issueInstallState), so the Install
          button now works anywhere. What still needs a host is the far end:
          GitHub's post-install redirect goes to the App's `setup_url`, which
          is frozen at App-creation time to
          `${appUrl}/api/integrations/github/install-callback` (see
          lib/githubManifest.ts) — a route only the host serves. An App
          provisioned from the desktop therefore needs that desktop running to
          land the callback; the manifest cannot be edited afterwards.

          Keep this list honest as routes migrate. Codex caught an earlier
          revision claiming GitHub was "fully available here" — a false
          availability claim inside the PR whose entire point is that the
          console must not make false availability claims. #278 then falsified
          this very note in the same way, from the other direction: it kept
          claiming a host was needed to START an install after the host had
          been cut out of that path. A stale limitation reads as honest and is
          not; re-read this block whenever the install flow moves.

          Two variants, because "absent" and "unhealthy" call for different
          things from the reader. Absent is the permanent, expected state on
          app.steerly.ai and the answer is the desktop app. Unhealthy means a
          host IS running and returned an error — telling that reader to install
          the desktop app they are already running is the mislabel Codex found
          on b04e3db.

          Informational, not an error, in both cases: dressing this in red
          taught users to distrust a console that is in fact working.
        */}
        {snapshot.hostUnavailable && !loading ? (
          <section className="admin-note" role="status">
            {snapshot.hostFailure === "unhealthy" ? (
              <>
                <strong>
                  The local Steerly host answered with an error
                  {snapshot.hostStatus ? ` (HTTP ${snapshot.hostStatus})` : ""}.
                </strong>
                <p>
                  A host is running — it just isn&rsquo;t serving the admin routes right now, so the
                  enforcement tier ladder and the host command log are empty. Everything below that
                  reads from Convex is unaffected. Retry, or check the host&rsquo;s logs.
                </p>
              </>
            ) : (
              <>
                <strong>Running without a local Steerly host.</strong>
                <p>
                  Policy, firewall, PR risk, agents, billing and team read live from Convex and are
                  fully available here, including the enforcement mode toggle. Three things need the
                  desktop app, which runs the host locally: the enforcement tier ladder, the host
                  command log, and <em>finishing</em> a new GitHub App installation — you can start
                  one here, but GitHub sends you back to the App&rsquo;s fixed setup URL, which only
                  the desktop app answers. The GitHub tab still shows the current connection and can
                  detect an existing one.
                </p>
              </>
            )}
          </section>
        ) : null}

        {/*
          Gate every pane behind `authorized`. Previously the unauthorized
          banner rendered alongside live, clickable controls (Firewall
          classifier, Reload policies, etc.), letting users trigger calls
          that immediately 401/403 and see a confusing dead UI. The topbar
          (tabs, Refresh, Workbench link) stays visible above so the user
          can re-attempt after fixing their session.
        */}
        {authorized ? (
          <>
            <AdminPane active={activeTab === "overview"}>
              <header className="admin-pane__head">
                <div>
                  <h2>Security control plane</h2>
                  <p className="muted-fg">
                    Clerk-authenticated policy operations, enforcement posture, and audit activity.
                  </p>
                </div>
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => void reloadPolicies()}
                >
                  <RefreshCw size={14} aria-hidden="true" />
                  Reload policies
                </button>
              </header>
              {reloadResult ? (
                <p className={truthy(reloadResult.ok) ? "admin-success" : "admin-error"}>
                  {truthy(reloadResult.ok)
                    ? `Reloaded ${displayValue(reloadResult.packCount)} policy packs.`
                    : displayValue(reloadResult.lastError)}
                </p>
              ) : null}
              <section className="admin-dashboard-grid" aria-label="Admin metrics">
                <MetricCard
                  icon={<FileJson size={16} />}
                  label="Policy packs"
                  value={packs.length}
                />
                <MetricCard
                  icon={<SlidersHorizontal size={16} />}
                  label="Firewall rules"
                  value={firewallDefaults.length + firewallAdmin.length}
                />
                <MetricCard
                  icon={<Database size={16} />}
                  label="PR policies"
                  value={prDefaults.length + prAdmin.length}
                />
                <MetricCard icon={<Terminal size={16} />} label="Agents" value={agentRows.length} />
                <MetricCard
                  icon={<Activity size={16} />}
                  label="Audit events"
                  value={auditEvents.length}
                />
              </section>
              <section className="admin-table-section">
                <header className="admin-table-section__head">
                  <h3>Recent activity</h3>
                  <span>{auditEvents.length} loaded</span>
                </header>
                <EventList events={auditEvents.slice(0, 5)} />
              </section>
            </AdminPane>

            <AdminPane active={activeTab === "packs"}>
              <header className="admin-pane__head">
                <div>
                  <h2>Policy packs</h2>
                  <p className="muted-fg">Built-in, org, and repo policy packs currently loaded.</p>
                </div>
              </header>
              <PackTable packs={packs} />
            </AdminPane>

            <AdminPane active={activeTab === "firewall"}>
              <header className="admin-pane__head">
                <div>
                  <h2>Command firewall</h2>
                  <p className="muted-fg">
                    Classify shell commands against the production policy path.
                  </p>
                </div>
              </header>
              <section className="admin-pane__test">
                <label>
                  <span>Test a command</span>
                  <input
                    value={testCommand}
                    onChange={(event) => setTestCommand(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void classifyCommand();
                    }}
                  />
                </label>
                <button
                  className="button secondary"
                  type="button"
                  disabled={testing}
                  onClick={() => void classifyCommand()}
                >
                  <Play size={14} aria-hidden="true" />
                  {testing ? "Classifying" : "Classify"}
                </button>
                {testResult ? (
                  <span className={`admin-test-result ${decisionClass(testResult.decision)}`}>
                    {displayValue(testResult.decision)} · {displayValue(testResult.reason)}
                  </span>
                ) : null}
                {testError ? <span className="admin-test-result is-error">{testError}</span> : null}
              </section>
              <RuleTable title="Default firewall rules" rules={firewallDefaults} />
              <RuleTable title="Admin firewall rules" rules={firewallAdmin} />
            </AdminPane>

            <AdminPane active={activeTab === "pr"}>
              <header className="admin-pane__head">
                <div>
                  <h2>PR risk policies</h2>
                  <p className="muted-fg">
                    Policies applied to pull-request scans and reviewer briefs.
                  </p>
                </div>
              </header>
              <RuleTable title="Default PR policies" rules={prDefaults} />
              <RuleTable title="Admin PR policies" rules={prAdmin} />
            </AdminPane>

            <AdminPane active={activeTab === "agents"}>
              <header className="admin-pane__head">
                <div>
                  <h2>Agent registry</h2>
                  <p className="muted-fg">
                    Built-in and custom agent commands with admin overrides.
                  </p>
                </div>
              </header>
              <AgentTable rows={agentRows} />
            </AdminPane>

            <AdminPane active={activeTab === "activity"}>
              <header className="admin-pane__head">
                <div>
                  <h2>Activity</h2>
                  <p className="muted-fg">The latest admin and security audit events.</p>
                </div>
              </header>
              {/* Pro-gated: reactive audit feed (api.audit.queries.recent),
                  not the security-gated REST snapshot — so pro users see it. */}
              <EventList events={activityEvents} />
            </AdminPane>

            <AdminPane active={activeTab === "enforcement"}>
              <EnforcementPane
                enforcement={enforcement}
                enforcementMode={enforcementModeData}
                settingMode={settingMode}
                modeError={modeError}
                onSetMode={async (mode) => {
                  setSettingMode(true);
                  setModeError(null);
                  try {
                    await setEnforcementModeMutation({ mode });
                  } catch (err) {
                    setModeError(err instanceof Error ? err.message : "Failed to update mode");
                  } finally {
                    setSettingMode(false);
                  }
                }}
              />
            </AdminPane>

            {/* Mounted only when active, NOT via AdminPane. AdminPane keeps
                inactive panes mounted, and listPatternMetadata is
                plan:"security"-gated — a base admin sitting on Plan & billing
                would fire it and collect a 402 for a tab they cannot see. */}
            {activeTab === "dlp-patterns" && (
              <AdminTabBoundary tab="dlp-patterns">
                <DlpPatternsTab />
              </AdminTabBoundary>
            )}
            {activeTab === "plan-billing" && (
              <AdminTabBoundary tab="plan-billing">
                <PlanBillingTab />
              </AdminTabBoundary>
            )}
            {activeTab === "team-roles" && (
              <AdminTabBoundary tab="team-roles">
                <TeamRolesTab />
              </AdminTabBoundary>
            )}
            {activeTab === "github-integration" && (
              <AdminTabBoundary tab="github-integration">
                <GithubIntegrationTab />
              </AdminTabBoundary>
            )}
            {activeTab === "credentials-vault" && (
              <AdminTabBoundary tab="credentials-vault">
                <CredentialsVaultTab />
              </AdminTabBoundary>
            )}
            {activeTab === "connect-agents" && (
              <AdminTabBoundary tab="connect-agents">
                <ConnectAgentsTab />
              </AdminTabBoundary>
            )}
            {activeTab === "cost-dashboard" && (
              <AdminTabBoundary tab="cost-dashboard">
                <CostDashboardTab />
              </AdminTabBoundary>
            )}
            {activeTab === "egress" && (
              <AdminTabBoundary tab="egress">
                <EgressAllowlistTab />
              </AdminTabBoundary>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}

// NOTE: this is a field-by-field copy, not a spread — every key of AdminSnapshot
// must be listed or it is silently dropped on the way to the store, with no type
// error (the object still satisfies AdminSnapshot because the missing keys are
// optional). Add new fields HERE as well as to the interface.
function setAdminSnapshot(next: AdminSnapshot) {
  adminStore.setState({
    authorized: next.authorized,
    packs: next.packs || [],
    auditEvents: next.auditEvents || [],
    firewall: next.firewall,
    pr: next.pr,
    agents: next.agents,
    enforcement: next.enforcement,
    error: next.error,
    hostUnavailable: next.hostUnavailable,
    hostFailure: next.hostFailure,
    hostStatus: next.hostStatus,
  });
}

function AdminPane({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <section className={`admin-pane${active ? " is-active" : ""}`} hidden={!active}>
      {children}
    </section>
  );
}

function MetricCard({ icon, label, value }: { icon?: ReactNode; label: string; value: ReactNode }) {
  return (
    <article className="admin-metric-card">
      <span>
        {icon}
        {label}
      </span>
      <strong>{value}</strong>
    </article>
  );
}

function PackTable({ packs }: { packs: Array<Record<string, unknown>> }) {
  if (!packs.length) return <EmptyState label="No policy packs loaded." />;
  return (
    <section className="admin-table-section">
      <table className="admin-grid">
        <thead>
          <tr>
            <th>Pack</th>
            <th>Layer</th>
            <th>Rules</th>
            <th>Priority</th>
            <th>Status</th>
            <th>Hash</th>
          </tr>
        </thead>
        <tbody>
          {packs.map((pack) => (
            <tr key={displayValue(pack.id)} className="admin-row">
              <td>
                <strong>{displayValue(pack.id)}</strong>
              </td>
              <td>{displayValue(pack.layer)}</td>
              <td>{displayValue(pack.ruleCount ?? pack.rules)}</td>
              <td>{displayValue(pack.priority)}</td>
              <td>
                <span
                  className={`admin-chip ${
                    truthy(pack.enforced) ? "admin-chip--allow" : "admin-chip--neutral"
                  }`}
                >
                  {truthy(pack.enforced) ? "enforced" : "observe"}
                </span>
              </td>
              <td>
                <code>{shortHash(pack.contentHash)}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RuleTable({ title, rules }: { title: string; rules: Array<Record<string, unknown>> }) {
  return (
    <section className="admin-table-section">
      <header className="admin-table-section__head">
        <h3>{title}</h3>
        <span>{rules.length} rules</span>
      </header>
      {!rules.length ? (
        <EmptyState label="No rules configured." />
      ) : (
        <table className="admin-grid">
          <thead>
            <tr>
              <th>ID</th>
              <th>Decision</th>
              <th>Reason</th>
              <th>Pattern</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule, index) => (
              <tr key={displayValue(rule.id) || index} className="admin-row">
                <td>
                  {/* `ruleId` is the human name ("cat-env-file"); `id` is the
                      Convex document id, which the admin rows were showing as
                      an opaque 32-char string. */}
                  <code>{displayValue(rule.ruleId || rule.id || rule.name)}</code>
                </td>
                <td>
                  <span className={`admin-chip ${decisionClass(rule.decision || rule.action)}`}>
                    {displayValue(rule.decision || rule.action)}
                  </span>
                </td>
                {/* firewall.queries.listRules emits the explanation as
                    `description` (builtins map it from their `why`). This cell
                    never read that field, so every built-in rule — including
                    all 7 denies — displayed "none" in the Reason column. */}
                <td>
                  {displayValue(
                    rule.reason || rule.description || rule.recommendation || rule.name
                  )}
                </td>
                <td>
                  <code>{displayValue(rule.pattern || rule.categories)}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function AgentTable({ rows }: { rows: Array<Record<string, unknown>> }) {
  if (!rows.length) return <EmptyState label="No agents registered." />;
  return (
    <section className="admin-table-section">
      <table className="admin-grid">
        <thead>
          <tr>
            <th>Agent</th>
            <th>Command</th>
            <th>Type</th>
            <th>Policy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${displayValue(row.type)}-${displayValue(row.name)}`} className="admin-row">
              <td>
                <strong>{displayValue(row.name)}</strong>
              </td>
              <td>
                <code>{displayValue(row.command)}</code>
              </td>
              <td>{displayValue(row.type)}</td>
              <td>{displayValue(row.policy)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function EventList({ events }: { events: Array<Record<string, unknown>> }) {
  if (!events.length) return <EmptyState label="No audit events." />;
  return (
    <div className="admin-activity">
      {events.map((event, index) => (
        <article key={displayValue(event.id) || index} className="admin-activity-row">
          <div className="admin-activity-row__head">
            <strong>{displayValue(event.type || event.kind || event.status)}</strong>
            <span>{formatTimestamp(event.recordedAt || event.createdAt || event.ts)}</span>
          </div>
          <div className="admin-activity-row__body">
            <span>
              {displayValue(event.detail || event.reason || event.command || event.policyId)}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return <div className="empty-state admin-empty-state">{label}</div>;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function truthy(value: unknown) {
  return value === true || value === "true" || value === "enabled";
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "none";
  if (Array.isArray(value)) return value.length ? value.map(displayValue).join(", ") : "none";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// Enforcement entries from the backend are tagged with `kind`:
//   - "tier"    → one of the four global-enforcement tiers (1..4)
//   - "posture" → a policy-engine / ingest / default-enforce row
// Anything without `kind` falls through to the legacy scalar renderer.
type TierState = "enabled" | "inactive" | "planned" | "error";
type TierEntry = {
  kind: "tier";
  tier: number;
  label: string;
  mechanism?: string;
  state: TierState;
  detail?: string;
  coverage?: string;
  enabled?: boolean;
};
type PostureEntry = {
  kind: "posture";
  label: string;
  state: string;
  detail?: string;
  enabled?: boolean;
};

function isTier(entry: unknown): entry is TierEntry {
  return Boolean(entry) && (entry as { kind?: string }).kind === "tier";
}
function isPosture(entry: unknown): entry is PostureEntry {
  return Boolean(entry) && (entry as { kind?: string }).kind === "posture";
}

// RC-5: valid enforcement modes with display labels and descriptions.
const ENFORCEMENT_MODES: Array<{ value: string; label: string; description: string }> = [
  {
    value: "off",
    label: "Off",
    description: "Policy engine disabled — all commands pass through.",
  },
  {
    value: "observe",
    label: "Observe",
    description: "Log policy matches without blocking. Zero agent friction.",
  },
  {
    value: "ask",
    label: "Ask",
    description: "Prompt for approval on high-risk commands. Blocks until a decision is made.",
  },
  { value: "block", label: "Block", description: "Deny commands that match a deny rule outright." },
];

function EnforcementPane({
  enforcement,
  enforcementMode,
  settingMode,
  modeError,
  onSetMode,
}: {
  enforcement: Record<string, unknown>;
  enforcementMode?: { mode: string; posture?: string; updatedBy?: string; updatedAt?: number };
  settingMode?: boolean;
  modeError?: string | null;
  onSetMode?: (mode: string) => void;
}) {
  const entries = Object.entries(enforcement);
  const tiers = entries
    .filter(([, v]) => isTier(v))
    .map(([key, v]) => [key, v as TierEntry] as const)
    .sort((a, b) => a[1].tier - b[1].tier);
  const posture = entries
    .filter(([, v]) => isPosture(v))
    .map(([key, v]) => [key, v as PostureEntry] as const);
  // RC-5: Legacy scalars that don't fit tier/posture shape (e.g. rcFiles,
  // shimInstalled path strings, tap process IDs). Render them as formatted
  // rows instead of a raw JSON dump.
  const legacy = entries.filter(([, v]) => !isTier(v) && !isPosture(v));

  // RC-5: The mode from the Convex query is the source of truth. Fall back
  // to the REST snapshot's mode field when the query is still loading.
  const currentMode: string =
    enforcementMode?.mode ?? (typeof enforcement.mode === "string" ? enforcement.mode : "off");

  return (
    <>
      <header className="admin-pane__head">
        <div>
          <h2>Enforcement posture</h2>
          <p className="muted-fg">
            Two axes: a four-tier visibility ladder for agent observation, plus the policy
            enforcement mode that turns observations into decisions.
          </p>
        </div>
      </header>

      {/* RC-5: Enforcement mode selector — writable (admin-gated via setEnforcementMode). */}
      <section className="admin-subsection">
        <header className="admin-subsection__head">
          <h3>
            <Shield size={16} aria-hidden="true" style={{ display: "inline", marginRight: 6 }} />
            Enforcement mode
          </h3>
          <p className="muted-fg">
            Controls how the policy engine responds to rule matches. Applied on the chat-send
            evaluation path and the egress allow-list surface. PATH-shim and LLM-tap stream paths
            honour the egress mode; full stream-path policy-pack enforcement wiring is in progress.
          </p>
        </header>
        {/* PR#128: native <input type="radio"> for keyboard nav (Tab/Arrow/Space work for free).
            The input is visually hidden (sr-only) and the <label> carries the card styling.
            name="enforcement-mode" groups them so the browser handles single-selection. */}
        <div className="admin-enforcement-modes" role="radiogroup" aria-label="Enforcement mode">
          {ENFORCEMENT_MODES.map((m) => {
            const isActive = currentMode === m.value;
            const inputId = `enforcement-mode-${m.value}`;
            return (
              <label
                key={m.value}
                htmlFor={inputId}
                className={`admin-enforcement-mode-card${isActive ? " is-active" : ""}`}
                data-mode={m.value}
              >
                <input
                  type="radio"
                  id={inputId}
                  name="enforcement-mode"
                  value={m.value}
                  checked={isActive}
                  disabled={settingMode}
                  aria-label={`Set enforcement mode to ${m.label}`}
                  className="sr-only"
                  onChange={() => onSetMode?.(m.value)}
                />
                <span className="admin-enforcement-mode-card__label">
                  {isActive ? (
                    <CheckCircle2 size={14} aria-hidden="true" />
                  ) : (
                    <span className="admin-enforcement-mode-card__dot" aria-hidden="true" />
                  )}
                  {m.label}
                </span>
                <span className="admin-enforcement-mode-card__desc">{m.description}</span>
              </label>
            );
          })}
        </div>
        {modeError ? (
          <p className="admin-error" role="alert" style={{ marginTop: 8 }}>
            <AlertCircle size={14} aria-hidden="true" style={{ marginRight: 4 }} />
            {modeError}
          </p>
        ) : null}
        {enforcementMode?.updatedBy || enforcementMode?.updatedAt ? (
          <p className="muted-fg" style={{ fontSize: "0.75rem", marginTop: 6 }}>
            Last updated
            {enforcementMode.updatedBy ? ` by ${enforcementMode.updatedBy}` : ""}
            {enforcementMode.updatedAt ? ` at ${formatTimestamp(enforcementMode.updatedAt)}` : ""}
          </p>
        ) : null}
      </section>

      {tiers.length ? (
        <section className="admin-subsection">
          <header className="admin-subsection__head">
            <h3>Agent visibility ladder</h3>
            <p className="muted-fg">
              Where Steerly intercepts agent activity. Higher tiers cost more to ship but resist
              adversarial bypass.
            </p>
          </header>
          <section className="admin-tier-grid">
            {tiers.map(([key, tier]) => (
              <EnforcementTierCard key={key} tier={tier} />
            ))}
          </section>
        </section>
      ) : null}

      {posture.length ? (
        <section className="admin-subsection">
          <header className="admin-subsection__head">
            <h3>Policy &amp; ingest posture</h3>
            <p className="muted-fg">Configuration and runtime state of the policy engine itself.</p>
          </header>
          <section className="admin-dashboard-grid">
            {posture.map(([key, row]) => (
              <PostureCard key={key} row={row} />
            ))}
          </section>
        </section>
      ) : null}

      {legacy.length ? (
        <section className="admin-subsection">
          <header className="admin-subsection__head">
            <h3>Host status</h3>
            <p className="muted-fg">Runtime install state reported by the Steerly host.</p>
          </header>
          <section className="admin-dashboard-grid">
            {legacy.map(([key, value]) => (
              <MetricCard key={key} label={humanize(key)} value={renderEnforcementValue(value)} />
            ))}
          </section>
        </section>
      ) : null}
    </>
  );
}

// Tier states map 1:1 to pill tones. Posture cards drive their own tone
// off the boolean `enabled` (see PostureCard) so they don't need a table.
const TIER_STATE_TONE: Record<TierState, string> = {
  enabled: "on",
  inactive: "off",
  planned: "planned",
  error: "error",
};

const TIER_STATE_LABEL: Record<TierState, string> = {
  enabled: "active",
  inactive: "inactive",
  planned: "planned",
  error: "error",
};

function EnforcementTierCard({ tier }: { tier: TierEntry }) {
  const tone = TIER_STATE_TONE[tier.state] || "off";
  const stateLabel = TIER_STATE_LABEL[tier.state] || tier.state;
  return (
    <article className="admin-tier-card" data-state={tier.state}>
      <header className="admin-tier-card__head">
        <span className="admin-tier-card__badge">Tier {tier.tier}</span>
        <span className="admin-metric-card__pill" data-tone={tone}>
          {stateLabel}
        </span>
      </header>
      <h4 className="admin-tier-card__label">{tier.label}</h4>
      {tier.mechanism ? <p className="admin-tier-card__mechanism">{tier.mechanism}</p> : null}
      {tier.detail ? <p className="admin-tier-card__detail">{tier.detail}</p> : null}
      {tier.coverage ? (
        <p className="admin-tier-card__coverage">
          <span className="admin-tier-card__coverage-label">Catches</span> {tier.coverage}
        </p>
      ) : null}
    </article>
  );
}

function PostureCard({ row }: { row: PostureEntry }) {
  // The pill tone is driven by the boolean `enabled` rather than the
  // state text. Posture `state` is a free-form label (e.g. "1 loaded",
  // "healthy", "missing") — looking it up in TIER_STATE_TONE would mis-
  // tone valid rows like "policies: 1 loaded" as grey. `error` state
  // overrides to red regardless.
  const tone = row.state === "error" ? "error" : row.enabled ? "on" : "off";
  return (
    <article className="admin-metric-card">
      <span>{row.label}</span>
      <strong>
        <span className="admin-metric-card__pill" data-tone={tone}>
          {row.state}
        </span>
      </strong>
      {row.detail ? <p className="admin-tier-card__detail">{row.detail}</p> : null}
    </article>
  );
}

/**
 * Per-card value rendering for the Enforcement pane. The raw enforcement
 * payload mixes scalars (booleans, numbers, paths) with arrays of objects
 * (rcFiles). Rendering all of them with the same 18px headline blew out
 * the grid: one card's multi-line JSON.stringify dump stretched the row,
 * leaving every other card mostly empty. Here we pick a treatment per
 * value shape so each card stays compact and the layout breathes.
 */
function renderEnforcementValue(value: unknown): ReactNode {
  if (value === true || value === false) {
    return (
      <span className="admin-metric-card__pill" data-tone={value ? "on" : "off"}>
        {value ? "true" : "false"}
      </span>
    );
  }
  if (value === null || value === undefined || value === "") {
    return (
      <span className="admin-metric-card__pill" data-tone="off">
        none
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (!value.length) {
      return (
        <span className="admin-metric-card__pill" data-tone="off">
          empty
        </span>
      );
    }
    // Render as a scrollable mono block with one entry per line. The
    // count prefix tells the user how many there are before they expand.
    return (
      <code className="admin-metric-card__mono" title={`${value.length} entries`}>
        {value
          .map((entry) =>
            typeof entry === "object" && entry !== null ? JSON.stringify(entry) : String(entry)
          )
          .join("\n")}
      </code>
    );
  }
  if (typeof value === "object") {
    return <code className="admin-metric-card__mono">{JSON.stringify(value, null, 2)}</code>;
  }
  const text = String(value);
  // Long paths / hashes get mono treatment so they wrap at any character
  // instead of forcing the column wider than the grid track.
  if (text.length > 28 || text.startsWith("/") || /[\\/]/.test(text)) {
    return <code className="admin-metric-card__mono">{text}</code>;
  }
  return text;
}

function shortHash(value: unknown) {
  const text = displayValue(value);
  return text === "none" ? "none" : text.slice(0, 12);
}

function decisionClass(value: unknown) {
  const decision = displayValue(value).toLowerCase();
  if (decision.includes("deny") || decision.includes("block")) return "admin-chip--deny";
  if (decision.includes("ask") || decision.includes("approval")) return "admin-chip--sev-high";
  if (decision.includes("allow") || decision.includes("comment")) return "admin-chip--allow";
  return "admin-chip--neutral";
}

function formatTimestamp(value: unknown) {
  if (!value) return "unknown";
  const date = new Date(typeof value === "number" ? value : String(value));
  if (Number.isNaN(date.getTime())) return displayValue(value);
  return date.toLocaleString();
}

function humanize(key: string) {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .toLowerCase();
}

function formatAdminError(message?: string) {
  const value = message || "Admin API unavailable.";
  // `unauthorized` (401) and `forbidden` (403) are different problems and the
  // fixes have nothing in common, but this branch reported both as "you need a
  // security plan". During the audit a local host missing CLERK_JWT_KEY 401'd
  // every /api/* route, and the console confidently blamed the subscription —
  // sending anyone debugging it down the wrong path entirely.
  if (/forbidden/i.test(value)) {
    return "This account does not have security-administration access. An org admin with the Security plan can grant it.";
  }
  if (/unauthorized|missing_token/i.test(value)) {
    return "The backend rejected this session's token (401). The session may have expired — sign in again. If it persists, the backend is not configured to verify Clerk tokens (CLERK_JWT_KEY missing on the host).";
  }
  return value;
}
